import { type CertificationPeriodViewModel, type RescheduledFrequency, type SearchFrequencyViewModel } from '@generated';
import { CalendarSync } from 'lucide-react';
import { CertificationPeriodCalendar } from '~/features/scheduling/cert-periods/CertificationPeriodCalendar';
import { convertFrequenciesToCalendarEvent, isSameCadenceCorrelator } from '../util';
import { applyReschedules, canChangeDate } from './util';

export function RescheduleFrequenciesCalendar({
	certificationPeriod,
	frequencies,
	rescheduled,
	highlightedFrequencyId,
	agencyStartOfWeek,
	onReschedule
}: {
	certificationPeriod: CertificationPeriodViewModel;
	frequencies: SearchFrequencyViewModel[];
	rescheduled: RescheduledFrequency[];
	highlightedFrequencyId?: string;
	agencyStartOfWeek: number;
	onReschedule: (change: RescheduledFrequency) => void;
}) {
	const events = convertFrequenciesToCalendarEvent(applyReschedules(frequencies, rescheduled)).map(x => {
		const frequency = frequencies.find(y => y.id === x.id)!;
		const wasRescheduled = rescheduled.some(y => y.frequencyId === x.id);
		return {
			...x,
			isReadonly: !frequency.invariants.canBeRescheduled,
			isHighlighted: x.id === highlightedFrequencyId,
			Icons: wasRescheduled ? [CalendarSync, ...(x.Icons ?? [])] : x.Icons
		};
	});

	const isDragAndDropValid = (frequencyId: string, newStartTime: Date) => {
		const frequency = frequencies.find(x => x.id === frequencyId)!;
		return (
			canChangeDate(frequency.frequencyCadenceTypeId) &&
			isSameCadenceCorrelator(frequency.frequencyCadenceTypeId, frequency.scheduledDateAndTime, newStartTime, agencyStartOfWeek)
		);
	};

	return (
		<CertificationPeriodCalendar
			events={events}
			certificationPeriod={certificationPeriod}
			allowDragAndDrop
			isDragAndDropValid={isDragAndDropValid}
			onDragStopped={(frequencyId, newStartTime) => onReschedule({ frequencyId, scheduledDateAndTime: newStartTime })}
			onCalendarItemClicked={event => {
				if (event.isReadonly || rescheduled.some(x => x.frequencyId === event.id)) {
					return;
				}
				onReschedule({ frequencyId: event.id, scheduledDateAndTime: event.startTime });
			}}
		/>
	);
}
